import { IntegrationCard } from '../Components/IntegrationCard';
import { IntegrationModal } from '../Components/IntegrationModal';
import { Content } from '../style';
import plug from '../../../../../assets/svg/actions/plug.svg';
import unplug from '../../../../../assets/svg/actions/unplug.svg';
import { CRMSetupSection } from './components/CRMSetupSection';
import { useRef, useState } from 'react';
import { useCrmIntegration } from '../../../../../features/crm-integration/hooks/use-crm-integration';
import { ConnectCRMModal } from './components/CRMSetupSection/CRMOrganizationSection/ConnectCRMModal';
import { useToast } from '../../../../../hooks/useToast';
import { Flex } from 'antd';
import { SuccessConnectionDataLabel } from '../Components/SuccessConnectionDataLabel';
import useFeatures from '../../../../../hooks/useFeatures';
import { SubscribeToTeamsPlanLink } from '../../../../../componentsV2/UpgradeToPro';

export const CrmIntegrationCard: React.FC = () => {
  const { error, success } = useToast();

  const [showModal, setShowModal] = useState(false);
  const crmRef = useRef<HTMLDivElement>(null);

  const { workspaceCrmIntegration, connect, disconnect } = useCrmIntegration();
  // const hasCrmConnected = false;
  const hasCrmConnected = Boolean(workspaceCrmIntegration?.workspace.integration);
  const features = useFeatures();
  const availableFeature = features.hubspotIntegration || features.salesforceIntegration;

  const provider = workspaceCrmIntegration?.workspace.integration?.provider;

  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowConnectModal, setShouldShowConnectModal] = useState(false);

  const handleConnectToCRM = async (selectedCRM: string) => {
    setIsLoading(true);
    try {
      const connectProvider = selectedCRM === 'SALESFORCE' ? 'salesforce' : 'hubspot';
      await connect(connectProvider);
    } catch (e) {
      error((e as Error).message);
    } finally {
      setIsLoading(false);
      setShouldShowConnectModal(false);
    }
  };

  const handleDisconnectToCRM = async () => {
    setIsLoading(true);
    try {
      await disconnect();
    } catch (e) {
      error((e as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <ConnectCRMModal
        isLoading={isLoading}
        isOpen={shouldShowConnectModal}
        onCancel={() => setShouldShowConnectModal(false)}
        onClose={() => setShouldShowConnectModal(false)}
        onConfirm={handleConnectToCRM}
      />

      <IntegrationCard
        title={
          hasCrmConnected
            ? `${provider === 'salesforce' ? 'Salesforce' : 'HubSpot'} CRM Setup`
            : 'CRM Setup'
        }
        description={
          hasCrmConnected
            ? `Manage your ${
                provider === 'salesforce' ? 'Salesforce' : 'HubSpot'
              } integration to have a better experience with your meetings recaps and insights.`
            : 'Connect your CRM to start receiving Smart Summaries from your meetings recaps.'
        }
        logoSrc={`https://assets.update.ai/${
          hasCrmConnected ? (provider === 'salesforce' ? 'salesforce' : 'hubspot') : 'crm-icon'
        }.png`}
        actions={[
          {
            type: 'button',
            label: hasCrmConnected ? 'Disconnect' : 'Connect',
            icon: (
              <img
                src={hasCrmConnected ? unplug : plug}
                alt="Connect"
                style={hasCrmConnected ? { width: '16px', height: '16px' } : {}}
              />
            ),
            onClick: () => {
              if (hasCrmConnected) {
                handleDisconnectToCRM();
              } else {
                setShouldShowConnectModal(true);
              }
            },
            tooltip: 'Only the Owner and Admins of the organization can change this setting.',
            buttonProps: {
              loading: isLoading,
              disabled: !availableFeature,
              style: {
                width: '110px',
                height: '34px',
              },
            },
          },
        ]}
      >
        <Content>
          {hasCrmConnected && (
            <Flex justify="space-between" style={{ width: '100%' }}>
              <SuccessConnectionDataLabel
                label="Connected: "
                value={workspaceCrmIntegration?.workspace.integration?.integrationEmail || ''}
              />
              <a style={{ color: '#FF6C54' }} onClick={() => setShowModal(true)}>
                {'Configure >'}
              </a>
            </Flex>
          )}
        </Content>

        <IntegrationModal
          show={showModal}
          onCancel={() => setShowModal(false)}
          icon={`https://assets.update.ai/${
            provider === 'salesforce' ? 'salesforce' : 'hubspot'
          }.png`}
          title={`${provider === 'salesforce' ? 'Salesforce' : 'HubSpot'} CRM Setup`}
          description="Organize your CRM integration by mapping the most important account properties to UpdateAI."
          showButton={false}
        >
          <CRMSetupSection ref={crmRef} onSave={() => setShowModal(false)} />
        </IntegrationModal>
      </IntegrationCard>
    </>
  );
};
